import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Radio, Headphones, X, AlertTriangle, Loader2 } from 'lucide-react';
import { listenAPI } from '../services/api.js';
import { formatPhone } from '../lib/format.js';

// LIVE CALLS + LISTEN-IN
//
// Brian: "I need to be able to see a call being called and calls that are currently
// online, and then listen into them in real time."
//
// Two separate capabilities, and they fail independently — which is why they are
// drawn separately:
//   1. SEEING what is live — a plain API read. Works today.
//   2. HEARING it — needs the browser to open a WebSocket straight to the LiveKit
//      server. See the blocked-state note below; this is an infrastructure fact,
//      not a bug in this file.
//
// ⛔ THE LISTENER IS SILENT BY CONSTRUCTION. The token core mints carries
// canPublish:false / canPublishData:false / hidden:true, and this player never
// asks for a microphone — no getUserMedia call exists in this file. The prospect
// cannot hear you and cannot see that anyone joined. That is enforced server-side
// in the grant, not here; this component could not publish even if it tried.

const POLL_MS = 10_000;

/** ⚠️ A browser cannot open ws:// from an https:// page — mixed-content is a hard
 *  block in every browser, not a warning. The box currently publishes LiveKit as
 *  `http://5.161.218.139:7880`, so the audio half stays unreachable until there is
 *  a TLS front door (wss://). Detected rather than assumed, so the day that lands
 *  this starts working with no code change. */
function wsBlockedReason(url) {
  if (!url) return 'Core did not return a LiveKit address.';
  const insecure = url.startsWith('http://') || url.startsWith('ws://');
  if (insecure && window.location.protocol === 'https:') {
    return `LiveKit is published over plain HTTP (${url}). A page served over HTTPS is not allowed to open an insecure WebSocket, so audio can’t connect until the box terminates TLS and core’s LIVEKIT_URL points at wss://.`;
  }
  return null;
}

function elapsed(since) {
  if (!since) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(since).getTime()) / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** The audio side. Mounted only while actively listening to one call. */
function Listener({ call, onClose }) {
  const [state, setState] = useState('connecting');
  const [error, setError] = useState(null);
  const roomRef = useRef(null);
  const holderRef = useRef(null);

  useEffect(() => {
    let dead = false;
    const attached = [];

    (async () => {
      let creds;
      try {
        const r = await listenAPI.token(call.id);
        creds = r?.data;
      } catch (err) {
        if (!dead) {
          setState('error');
          setError(err?.body?.message || err.message || 'Could not get a listen token.');
        }
        return;
      }
      if (dead) return;

      const blocked = wsBlockedReason(creds?.url);
      if (blocked) { setState('blocked'); setError(blocked); return; }

      try {
        // Imported lazily so the LiveKit client (a large bundle) is only fetched
        // when someone actually listens, not on every dashboard load.
        const { Room, RoomEvent } = await import('livekit-client');
        const room = new Room();
        roomRef.current = room;

        // Attach every audio track that arrives. This is subscribe-only: the
        // component never publishes, and the token forbids it regardless.
        room.on(RoomEvent.TrackSubscribed, (track) => {
          if (track.kind !== 'audio') return;
          const el = track.attach();
          el.autoplay = true;
          if (holderRef.current) holderRef.current.appendChild(el);
          attached.push({ track, el });
          if (!dead) setState('listening');
        });
        room.on(RoomEvent.Disconnected, () => { if (!dead) setState('ended'); });

        await room.connect(creds.url.replace(/^http/, 'ws'), creds.token);
        if (!dead) setState((s) => (s === 'connecting' ? 'waiting' : s));
      } catch (err) {
        if (!dead) {
          setState('error');
          setError(err?.message || 'Could not connect to the call audio.');
        }
      }
    })();

    return () => {
      dead = true;
      attached.forEach(({ track, el }) => { try { track.detach(el); el.remove(); } catch { /* gone already */ } });
      try { roomRef.current?.disconnect(); } catch { /* not connected */ }
    };
  }, [call.id]);

  const label = {
    connecting: 'Connecting…',
    waiting: 'Connected — waiting for audio',
    listening: 'Listening',
    ended: 'The call ended',
    blocked: 'Audio unavailable',
    error: 'Could not listen'
  }[state];

  return (
    <div className="mt-2 rounded-lg border border-stone-700 bg-stone-900/60 px-3 py-2">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {state === 'connecting' && <Loader2 size={14} className="animate-spin text-stone-400" />}
        {state === 'listening' && <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-emerald-400" />}
        <span className={state === 'listening' ? 'text-emerald-300' : 'text-stone-300'}>{label}</span>
        <span className="text-xs text-stone-500">· you are muted and invisible to the call</span>
        <button onClick={onClose} className="ml-auto text-stone-400 hover:text-stone-200" title="Stop listening">
          <X size={14} />
        </button>
      </div>
      {error && (
        <p className="mt-2 flex items-start gap-2 text-xs text-amber-200">
          <AlertTriangle size={12} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </p>
      )}
      <div ref={holderRef} className="hidden" />
    </div>
  );
}

export default function LiveCalls() {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ liveknown: true, configured: true });
  const [loaded, setLoaded] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [listening, setListening] = useState(null);
  const [, tick] = useState(0);

  useEffect(() => {
    let dead = false;
    const load = async () => {
      try {
        const r = await listenAPI.live();
        if (dead) return;
        setRows(r?.data || []);
        setMeta({ liveknown: r?.liveknown !== false, configured: r?.configured !== false });
      } catch (err) {
        // 404 = core hasn't shipped the route yet. Hide rather than shout.
        if (!dead && (err?.status === 404 || err?.status === 501)) setUnavailable(true);
      } finally {
        if (!dead) setLoaded(true);
      }
    };
    load();
    const poll = setInterval(load, POLL_MS);
    const clock = setInterval(() => tick((n) => n + 1), 1000); // running timers
    return () => { dead = true; clearInterval(poll); clearInterval(clock); };
  }, []);

  // A call that drops off the live list must not keep an open audio session.
  useEffect(() => {
    if (listening && !rows.some((r) => r.id === listening)) setListening(null);
  }, [rows, listening]);

  if (unavailable || !loaded || rows.length === 0) return null;

  return (
    <section className="card mb-6">
      <header className="mb-3 flex flex-wrap items-center gap-2">
        <Radio size={18} className="text-emerald-400" />
        <h2 className="text-xl text-stone-100">On a call now</h2>
        <span className="badge border border-emerald-600/40 bg-emerald-600/20 text-[10px] text-emerald-200">
          {rows.length} live
        </span>
      </header>

      {/* Said out loud, because a list that looks authoritative while LiveKit is
          unreachable is worse than one that admits it is guessing. */}
      {!meta.liveknown && (
        <p className="mb-3 flex items-start gap-2 rounded-lg border border-amber-600/40 bg-amber-950/30 px-3 py-2 text-sm text-amber-200">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          Couldn’t reach LiveKit to confirm these, so this list is from call records
          alone — one of them may have already ended.
        </p>
      )}

      <ul className="divide-y divide-stone-800">
        {rows.map((r) => (
          <li key={r.id} className="py-2 text-sm">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span className="h-2 w-2 shrink-0 animate-pulse rounded-full bg-emerald-400" />
              <span className="text-stone-200">{r.contactName || formatPhone(r.toNumber)}</span>
              {r.contactName && <span className="text-stone-500">{formatPhone(r.toNumber)}</span>}
              <span className="text-xs text-stone-500">
                {r.direction === 'outbound' ? 'outbound' : 'inbound'} · {r.status}
                {elapsed(r.answeredAt || r.startedAt) ? ` · ${elapsed(r.answeredAt || r.startedAt)}` : ''}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <Link to={`/calls/${r.id}`} className="text-xs text-stone-500 underline-offset-2 hover:text-alfred-gold hover:underline">
                  Open
                </Link>
                {/* ⛔ No Listen button when core can't reach the audio. Production
                    dials box-first, and on that path core never learns the LiveKit
                    room — so offering a button that always errors would be worse
                    than not offering one. The reason is stated instead. */}
                {r.canListen ? (
                  <button
                    onClick={() => setListening(listening === r.id ? null : r.id)}
                    className="inline-flex items-center gap-1.5 rounded-md border border-stone-700 px-2 py-1 text-xs text-stone-200 hover:border-alfred-gold hover:text-alfred-gold"
                  >
                    <Headphones size={12} />
                    {listening === r.id ? 'Stop' : 'Listen'}
                  </button>
                ) : (
                  <span
                    className="inline-flex items-center gap-1.5 rounded-md border border-stone-800 px-2 py-1 text-xs text-stone-600"
                    title="This call was placed over the box-first path, which doesn't report its LiveKit room to the dashboard, so there is nothing to attach audio to yet."
                  >
                    <Headphones size={12} />
                    No audio
                  </span>
                )}
              </div>
            </div>
            {listening === r.id && <Listener call={r} onClose={() => setListening(null)} />}
          </li>
        ))}
      </ul>
    </section>
  );
}
